import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { TransporterPackage } from '@otw/models'
import { TransportTruck } from '@otw/models/logistics'
import { transporterPackageConverter, transportTruckConverter } from '@otw/models/converters'
import { buildSafetyAndTimelineMaps } from './buildInventoryV3Maps'

const packageCreatedMs = (pkg: TransporterPackage): number => pkg.created_at?.toMillis?.() ?? 0

/**
 * Safety icons + timeline flags for the v3 grid. Uses the same React Query keys as
 * transporter admin / logistics so those pages share cache, without the
 * packages-with-vehicles getDoc fan-out or logistics vehicle hydration.
 */
export const useInventoryV3SafetyTimeline = () => {
  const { data: packages = [] } = useQuery({
    queryKey: ['transporter_packages'],
    queryFn: async (): Promise<TransporterPackage[]> => {
      const packagesCollection = collection(db, 'transporter_packages').withConverter(transporterPackageConverter)
      const snapshot = await getDocs(query(packagesCollection, orderBy('created_at', 'desc')))
      return snapshot.docs.map(docSnap => ({ ...docSnap.data(), id: docSnap.id }))
    },
  })

  const { data: transportTrucks = [] } = useQuery({
    queryKey: ['transport_trucks'],
    queryFn: async (): Promise<TransportTruck[]> => {
      const trucksCollection = collection(db, 'transport_trucks').withConverter(transportTruckConverter)
      const snapshot = await getDocs(query(trucksCollection, orderBy('created_at', 'desc')))
      return snapshot.docs.map(docSnap => docSnap.data())
    },
  })

  const packagesByVehicleId = useMemo(() => {
    const map = new Map<string, TransporterPackage>()
    packages.forEach(pkg => {
      if (pkg.archive) return
      const createdMs = packageCreatedMs(pkg)
      ;(pkg.vehicle_ids ?? []).forEach(vehicleId => {
        const existing = map.get(vehicleId)
        if (!existing || createdMs > packageCreatedMs(existing)) {
          map.set(vehicleId, pkg)
        }
      })
    })
    return map
  }, [packages])

  return useMemo(
    () => buildSafetyAndTimelineMaps(packagesByVehicleId, transportTrucks),
    [packagesByVehicleId, transportTrucks],
  )
}
